import { useEffect, useRef } from 'react'
import { emitToParent } from './embed-bridge'

/**
 * Tell an embedding application how the meeting's connections stand.
 *
 * Reads what `useMeetingConnection` already exposes and adds no state, so it
 * costs no renders; each effect fires only when its summary changes, and every
 * emit is a no-op outside an active frame. Coarse on purpose — the host needs
 * "are we up", not our internals.
 */
export function useEmbedConnectionEvents(s: {
  connected: boolean
  channelJoined: boolean
  sfuReady: boolean
  error: string | null | undefined
}): void {
  // "Reconnecting" only once there has been a connection to lose.
  const everConnected = useRef(false)
  if (s.channelJoined) everConnected.current = true
  const signaling = s.channelJoined
    ? 'connected'
    : s.connected
      ? 'joining'
      : everConnected.current
        ? 'reconnecting'
        : 'connecting'
  const media = s.error ? 'failed' : s.sfuReady ? 'connected' : 'connecting'
  const failed = !!s.error

  useEffect(() => {
    emitToParent('connection-state-changed', { signaling, media })
  }, [signaling, media])

  useEffect(() => {
    if (failed) emitToParent('error', { code: 'MEDIA_CONNECTION_FAILED' })
  }, [failed])
}
